import { useEffect } from 'react';

import { useAuthStore } from '@/stores/auth-store';

type Theme = 'light' | 'dark' | 'system';

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

interface UseThemeReturn {
  theme: Theme;
  setTheme: (theme: Theme) => Promise<void>;
}

export function useTheme(): UseThemeReturn {
  const preferences = useAuthStore((s) => s.preferences);
  const updatePreferences = useAuthStore((s) => s.updatePreferences);
  const theme: Theme = preferences?.theme ?? 'dark';

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // When in system mode, listen for OS preference changes so the CSS
  // media query re-evaluates correctly.  The data-theme stays "system" —
  // CSS handles the actual color swap via @media (prefers-color-scheme).
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  async function setTheme(t: Theme): Promise<void> {
    await updatePreferences({ theme: t });
  }

  return { theme, setTheme };
}
